import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Phone, MapPin, Save, Edit2, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface UserProfile {
    id: string;
    email: string;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    isVerified: boolean;
    role: string;
    client?: {
        phoneNumber: string | null;
        address: string | null;
    };
}

const Profile = () => {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        username: '',
        phoneNumber: '',
        address: '',
    });

    // Fetch user profile
    const { data: profile, isLoading } = useQuery<UserProfile>({
        queryKey: ['user-profile'],
        queryFn: async () => {
            const response = await api.get('/users/me');
            const data = response.data;
            // Set form data when profile loads
            setFormData({
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                username: data.username || '',
                phoneNumber: data.client?.phoneNumber || '',
                address: data.client?.address || '',
            });
            return data;
        },
    });

    // Update profile mutation
    const updateMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            const response = await api.put('/users/me', {
                firstName: data.firstName,
                lastName: data.lastName,
                username: data.username,
                client: {
                    phoneNumber: data.phoneNumber || null,
                    address: data.address || null,
                },
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            toast.success('Profile updated successfully!');
            setIsEditing(false);
        },
        onError: () => {
            toast.error('Failed to update profile');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        updateMutation.mutate(formData);
    };

    const handleCancel = () => {
        if (profile) {
            setFormData({
                firstName: profile.firstName || '',
                lastName: profile.lastName || '',
                username: profile.username || '',
                phoneNumber: profile.client?.phoneNumber || '',
                address: profile.client?.address || '',
            });
        }
        setIsEditing(false);
    };

    if (isLoading) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            {/* Header */}
            <div className="mb-6 sm:mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 flex items-center">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 mr-3 text-emerald-600" />
                    My Profile
                </h1>
                <p className="text-gray-600 text-lg">Manage your personal information</p>
            </div>

            {/* Profile Card */}
            {/* On a phone the name block and Edit Profile stack rather than
                share a row: side by side they pushed the button out to 461px
                on a 390px screen and the page scrolled sideways. */}
            <Card className="p-5 sm:p-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <div className="flex min-w-0 items-center space-x-4">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 bg-linear-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                            <span className="text-2xl sm:text-3xl font-bold text-white">
                                {profile?.firstName?.charAt(0) || 'U'}
                                {profile?.lastName?.charAt(0) || ''}
                            </span>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                                {profile?.firstName} {profile?.lastName}
                            </h2>
                            <p className="text-gray-600 break-all">{profile?.email}</p>
                            {profile?.isVerified ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                                    Verified
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                                    Not Verified
                                </span>
                            )}
                        </div>
                    </div>
                    {!isEditing && (
                        <Button
                            onClick={() => setIsEditing(true)}
                            variant="outline"
                            className="self-start sm:self-auto"
                        >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Profile
                        </Button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Label htmlFor="firstName">First Name</Label>
                            <div className="relative mt-1">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="firstName"
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) =>
                                        setFormData({ ...formData, firstName: e.target.value })
                                    }
                                    disabled={!isEditing}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="lastName">Last Name</Label>
                            <div className="relative mt-1">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="lastName"
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) =>
                                        setFormData({ ...formData, lastName: e.target.value })
                                    }
                                    disabled={!isEditing}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="username">Username</Label>
                            <div className="relative mt-1">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="username"
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) =>
                                        setFormData({ ...formData, username: e.target.value })
                                    }
                                    disabled={!isEditing}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="email">Email</Label>
                            <div className="relative mt-1">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={profile?.email || ''}
                                    disabled
                                    className="pl-10 bg-gray-50"
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>

                        <div>
                            <Label htmlFor="phoneNumber">Phone Number</Label>
                            <div className="relative mt-1">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    id="phoneNumber"
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) =>
                                        setFormData({ ...formData, phoneNumber: e.target.value })
                                    }
                                    disabled={!isEditing}
                                    className="pl-10"
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="role">Role</Label>
                            <div className="relative mt-1">
                                <Input
                                    id="role"
                                    type="text"
                                    value={profile?.role || ''}
                                    disabled
                                    className="bg-gray-50"
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Role cannot be changed</p>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="address">Address</Label>
                        <div className="relative mt-1">
                            <MapPin className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                            <textarea
                                id="address"
                                value={formData.address}
                                onChange={(e) =>
                                    setFormData({ ...formData, address: e.target.value })
                                }
                                disabled={!isEditing}
                                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 min-h-20"
                                placeholder="123 Main St, City, State 12345"
                            />
                        </div>
                    </div>

                    {isEditing && (
                        <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                            <Button type="button" variant="outline" onClick={handleCancel}>
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={updateMutation.isPending}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    )}
                </form>
            </Card>
        </div>
    );
};

export default Profile;
